import {
  Budget,
  NFMMOption,
  TotalByType,
  versionInfo,
  VersionTotals,
} from "../../types/budget/budget";
import {
  AppliedTaxes,
  DefaultTax,
  ServiceData,
  serviceValues,
  ServiceValues,
} from "../../types/budget/service-item";
import { resolveEffectiveServiceExchange } from "../../utils/exchangeManager";
import { roundHalfUp } from "../../utils/unitValue";

export enum TypesTaxEnum {
  PERCENTAGE = "PERCENTAGE",
  PERCENTAGE_RJ = "PERCENTAGE_RJ",
  FIX_VALUE = "FIX_VALUE",
  CORTESY = "CORTESY",
  DAILY = "DAILY",
  SERVICE = "SERVICE",
  INCLUDED = "INCLUDED",
  AC_CLIENT = "AC_CLIENT",
}

const zeroTotalTariffTypes = ["CORTESY", "AC_CLIENT", "INCLUDED"];

export function calculateAndMapService(
  service: ServiceData,
  budget: Budget,
  versionInfo: versionInfo
) {
  // Guard clause: ensure versionInfo is defined
  if (!versionInfo) {
    console.error("versionInfo is undefined in calculateAndMapService");
    // Return service as-is if versionInfo is missing
    return service;
  }

  // Store versionInfo in local constants to prevent any potential issues
  const currentVersion = versionInfo.version ?? 0;
  const currentStatus = versionInfo.status || "OPEN";
  const comparisonVersion = versionInfo.comparisonVersion ?? 0;

  // make it possible to calc the totals in this function
  // to reduce one loop further down the sequence of functions
  const serviceBaseValue =
    budget.baseValues?.find(
      (el) =>
        el.calcOption === service.calcOption &&
        el.supplierId === service.supplierId &&
        el.category === service.type &&
        el.version === currentVersion
    ) || baseValue;

  const numberOfServices = budget.budgetServices.filter((el) => {
    const sv = el.budgetValues;
    const tariffType = sv?.taxData?.tariff?.typeTax;
    if (zeroTotalTariffTypes.includes(tariffType ?? "")) {
      return false;
    }
    const daily = Number(sv?.daily);
    const quantity = Number(sv?.quantity);
    if (!daily || !quantity) {
      return false;
    }
    return (
      el.calcOption === service.calcOption &&
      el.type === service.type &&
      el.supplierId === service.supplierId
    );
  }).length;

  const currentServiceValues =
    currentStatus === "APPROVED"
      ? service.closingValues
      : currentVersion > 0
      ? service.negotiationValue?.[currentVersion - 1]
      : service.budgetValues;
  const resolvedExchange = resolveEffectiveServiceExchange(
    currentServiceValues,
    serviceBaseValue.exchange
  );

  switch (true) {
    case currentStatus === "APPROVED":
      //closing
      const calculatedClosing = calculateServiceTotals(
        {
          ...service.closingValues,
          taxData: {
            ...serviceValues.taxData,
            ...serviceBaseValue.taxData,
            tariff: service.closingValues.taxData.tariff,
          },
          totals: { totalInBRL: 0, totalInCurrency: 0 },
          exchangeData: resolvedExchange,
          appliedTaxes: {
            ...serviceValues.appliedTaxes,
            ...service.closingValues.appliedTaxes,
          },
        },
        service.calcOption,
        numberOfServices
      );
      return {
        ...service,
        closingValues: {
          ...service.closingValues,
          taxData: calculatedClosing.taxData,
          totals: calculatedClosing.totals,
          exchangeData: calculatedClosing.exchangeData,
        },
        calcOption: service.calcOption,
      } as ServiceData;
    case currentStatus === "NEGOTIATE":
      // negotiation
      const calculatedNegotiation = calculateServiceTotals(
        {
          ...service.negotiationValue[currentVersion - 1],
          taxData: {
            ...serviceValues.taxData,
            ...serviceBaseValue.taxData,
            tariff: service.negotiationValue[currentVersion - 1].taxData.tariff,
          },
          totals: { totalInBRL: 0, totalInCurrency: 0 },
          exchangeData: resolvedExchange,
          appliedTaxes: {
            ...serviceValues.appliedTaxes,
            ...service.negotiationValue[currentVersion - 1].appliedTaxes,
          },
        },
        service.calcOption,
        numberOfServices
      );
      return {
        ...service,
        negotiationValue: service.negotiationValue.map((el, index) => {
          if (index === currentVersion - 1) {
            return {
              ...el,
              taxData: calculatedNegotiation.taxData,
              totals: calculatedNegotiation.totals,
              exchangeData: calculatedNegotiation.exchangeData,
            };
          } else return el;
        }),
        calcOption: service.calcOption,
      } as ServiceData;

    case currentVersion === 0 && currentStatus !== "APPROVED":
    default:
      // budget
      const calculated = calculateServiceTotals(
        {
          ...service.budgetValues,
          taxData: {
            ...serviceValues.taxData,
            ...serviceBaseValue.taxData,
            tariff: service.budgetValues.taxData.tariff,
            extras: service.budgetValues.taxData.extras,
          },

          totals: { totalInBRL: 0, totalInCurrency: 0 },
          exchangeData: resolvedExchange,
          appliedTaxes: {
            ...serviceValues.appliedTaxes,
            ...service.budgetValues.appliedTaxes,
          },
        },
        service.calcOption,
        numberOfServices
      );
      return {
        ...service,
        budgetValues: {
          ...service.budgetValues,
          taxData: calculated.taxData,
          totals: calculated.totals,
          exchangeData: calculated.exchangeData,
        },
        calcOption: service.calcOption,
      } as ServiceData;
  }
}

export function calculateDetailedTotals(
  budgetServices: ServiceData[],
  key: "closingValues" | "budgetValues",
  extras: DefaultTax
): VersionTotals {
  const emptyTotal = createEmptyVersionTotals();

  for (const service of budgetServices) {
    // valida se já tem um supplier com valores
    const supplierIndex = emptyTotal.byCalculationType[service.calcOption][
      service.type as BudgetItemType
    ].findIndex((el) => el.supplierId === service.supplierId);
    const serviceTaxData = service[key].taxData;
    const supplierTributeTaxData =
      serviceTaxData.supplier_tribute ?? serviceValues.taxData.supplier_tribute;
    const serviceRevenueBRL =
      serviceTaxData.fee.valueBRL +
      serviceTaxData.markup.valueBRL +
      serviceTaxData.overhead.valueBRL +
      serviceTaxData.commission.valueBRL +
      serviceTaxData.incentive.valueBRL;

    const serviceTaxesCurrency =
      serviceTaxData.iss.currencyValue +
      serviceTaxData.tribute.currencyValue +
      serviceTaxData.localTax.currencyValue;

    const serviceTaxesBRL =
      serviceTaxData.iss.valueBRL +
      serviceTaxData.tribute.valueBRL +
      serviceTaxData.localTax.valueBRL;

    const serviceRevenueConverted =
      serviceTaxData.fee.currencyValue +
      serviceTaxData.markup.currencyValue +
      serviceTaxData.overhead.currencyValue +
      serviceTaxData.commission.currencyValue +
      serviceTaxData.incentive.currencyValue;

    // adiciona instancia total splited em byCalcType
    //se existe index para o fornecedor em questão soma os valores
    if (supplierIndex > -1) {
      const supplierTotal =
        emptyTotal.byCalculationType[service.calcOption][
          service.type as BudgetItemType
        ][supplierIndex];

      emptyTotal.byCalculationType[service.calcOption][
        service.type as BudgetItemType
      ][supplierIndex] = {
        ...supplierTotal,
        totals: {
          totalInBRL:
            supplierTotal.totals.totalInBRL + service[key].totals.totalInBRL,
          totalInCurrency:
            supplierTotal.totals.totalInCurrency +
            service[key].totals.totalInCurrency,
        },
        taxes: {
          totalInBRL: supplierTotal.taxes.totalInBRL + serviceTaxesBRL,
          totalInCurrency:
            supplierTotal.taxes.totalInCurrency + serviceTaxesCurrency,
        },
        tariffTotals: {
          totalInBRL:
            supplierTotal.tariffTotals.totalInBRL +
            serviceTaxData.tariff.valueBRL,
          totalInCurrency:
            supplierTotal.tariffTotals.totalInCurrency +
            serviceTaxData.tariff.valueBRL,
        },
        revenueTotals: {
          totalInBRL:
            supplierTotal.revenueTotals.totalInBRL + serviceRevenueBRL,
          totalInCurrency:
            supplierTotal.revenueTotals.totalInCurrency +
            serviceRevenueConverted,
        },
        serviceTaxTotals: {
          totalInBRL:
            supplierTotal.serviceTaxTotals.totalInBRL +
            serviceTaxData.serviceTax.valueBRL,
          totalInCurrency:
            supplierTotal.serviceTaxTotals.totalInCurrency +
            serviceTaxData.serviceTax.currencyValue,
        },
        localTaxTotals: {
          totalInBRL:
            supplierTotal.localTaxTotals.totalInBRL +
            serviceTaxData.localTax.valueBRL,
          totalInCurrency:
            supplierTotal.localTaxTotals.totalInCurrency +
            serviceTaxData.localTax.currencyValue,
        },
        tourismTaxTotals: {
          totalInBRL:
            supplierTotal.tourismTaxTotals.totalInBRL +
            serviceTaxData.tourismTax.valueBRL,
          totalInCurrency:
            supplierTotal.tourismTaxTotals.totalInCurrency +
            serviceTaxData.tourismTax.currencyValue,
        },
        iofTotals: {
          totalInBRL:
            supplierTotal.iofTotals.totalInBRL + serviceTaxData.iof.valueBRL,
          totalInCurrency:
            supplierTotal.iofTotals.totalInCurrency +
            serviceTaxData.iof.currencyValue,
        },
        feeTotals: {
          totalInBRL:
            supplierTotal.feeTotals.totalInBRL + serviceTaxData.fee.valueBRL,
          totalInCurrency:
            supplierTotal.feeTotals.totalInCurrency +
            serviceTaxData.fee.currencyValue,
        },
        issTotals: {
          totalInBRL:
            supplierTotal.issTotals.totalInBRL + serviceTaxData.iss.valueBRL,
          totalInCurrency:
            supplierTotal.issTotals.totalInCurrency +
            serviceTaxData.iss.currencyValue,
        },
        cbsTotals: {
          totalInBRL:
            (supplierTotal.cbsTotals?.totalInBRL ?? 0) +
            (serviceTaxData.cbs?.valueBRL ?? 0),
          totalInCurrency:
            (supplierTotal.cbsTotals?.totalInCurrency ?? 0) +
            (serviceTaxData.cbs?.currencyValue ?? 0),
        },
        ibsTotals: {
          totalInBRL:
            (supplierTotal.ibsTotals?.totalInBRL ?? 0) +
            (serviceTaxData.ibs?.valueBRL ?? 0),
          totalInCurrency:
            (supplierTotal.ibsTotals?.totalInCurrency ?? 0) +
            (serviceTaxData.ibs?.currencyValue ?? 0),
        },
        overheadTotals: {
          totalInBRL:
            supplierTotal.overheadTotals.totalInBRL +
            serviceTaxData.overhead.valueBRL,
          totalInCurrency:
            supplierTotal.overheadTotals.totalInCurrency +
            serviceTaxData.overhead.currencyValue,
        },
        markupTotals: {
          totalInBRL:
            supplierTotal.markupTotals.totalInBRL +
            serviceTaxData.markup.valueBRL,
          totalInCurrency:
            supplierTotal.markupTotals.totalInCurrency +
            serviceTaxData.markup.currencyValue,
        },
        commissionTotals: {
          totalInBRL:
            supplierTotal.commissionTotals.totalInBRL +
            serviceTaxData.commission.valueBRL,
          totalInCurrency:
            supplierTotal.commissionTotals.totalInCurrency +
            serviceTaxData.commission.currencyValue,
        },
        incentiveTotals: {
          totalInBRL:
            supplierTotal.incentiveTotals.totalInBRL +
            serviceTaxData.incentive.valueBRL,
          totalInCurrency:
            supplierTotal.incentiveTotals.totalInCurrency +
            serviceTaxData.incentive.currencyValue,
        },
        tributeTotals: {
          totalInBRL:
            supplierTotal.tributeTotals.totalInBRL +
            serviceTaxData.tribute.valueBRL,
          totalInCurrency:
            supplierTotal.tributeTotals.totalInCurrency +
            serviceTaxData.tribute.currencyValue,
        },
        supplierTributeTotals: {
          totalInBRL:
            supplierTotal.supplierTributeTotals.totalInBRL +
            supplierTributeTaxData.valueBRL,
          totalInCurrency:
            supplierTotal.supplierTributeTotals.totalInCurrency +
            supplierTributeTaxData.currencyValue,
        },
      };
    } else {
      emptyTotal.byCalculationType[service.calcOption][
        service.type as BudgetItemType
      ].push({
        supplierId: service.supplierId,
        calcOption: service.calcOption,
        taxes: {
          totalInBRL: serviceTaxesBRL,
          totalInCurrency: serviceTaxesCurrency,
        },
        revenueTotals: {
          totalInBRL: serviceRevenueBRL,
          totalInCurrency: serviceRevenueConverted,
        },
        serviceTaxTotals: {
          totalInBRL: serviceTaxData.serviceTax.valueBRL,
          totalInCurrency: serviceTaxData.serviceTax.currencyValue,
        },
        tariffTotals: {
          totalInBRL: serviceTaxData.tariff.valueBRL,
          totalInCurrency: serviceTaxData.tariff.currencyValue,
        },
        localTaxTotals: {
          totalInBRL: serviceTaxData.localTax.valueBRL,
          totalInCurrency: serviceTaxData.localTax.currencyValue,
        },
        tourismTaxTotals: {
          totalInBRL: serviceTaxData.tourismTax.valueBRL,
          totalInCurrency: serviceTaxData.tourismTax.currencyValue,
        },
        doubleChargedTotals: {
          totalInBRL: serviceTaxData.doubleCharged.valueBRL,
          totalInCurrency: serviceTaxData.doubleCharged.currencyValue,
        },
        iofTotals: {
          totalInBRL: serviceTaxData.iof.valueBRL,
          totalInCurrency: serviceTaxData.iof.currencyValue,
        },
        feeTotals: {
          totalInBRL: serviceTaxData.fee.valueBRL,
          totalInCurrency: serviceTaxData.fee.currencyValue,
        },
        issTotals: {
          totalInBRL: serviceTaxData.iss.valueBRL,
          totalInCurrency: serviceTaxData.iss.currencyValue,
        },
        cbsTotals: {
          totalInBRL: serviceTaxData.cbs?.valueBRL ?? 0,
          totalInCurrency: serviceTaxData.cbs?.currencyValue ?? 0,
        },
        ibsTotals: {
          totalInBRL: serviceTaxData.ibs?.valueBRL ?? 0,
          totalInCurrency: serviceTaxData.ibs?.currencyValue ?? 0,
        },
        overheadTotals: {
          totalInBRL: serviceTaxData.overhead.valueBRL,
          totalInCurrency: serviceTaxData.overhead.currencyValue,
        },
        markupTotals: {
          totalInBRL: serviceTaxData.markup.valueBRL,
          totalInCurrency: serviceTaxData.markup.currencyValue,
        },
        commissionTotals: {
          totalInBRL: serviceTaxData.commission.valueBRL,
          totalInCurrency: serviceTaxData.commission.currencyValue,
        },
        incentiveTotals: {
          totalInBRL: serviceTaxData.incentive.valueBRL,
          totalInCurrency: serviceTaxData.incentive.currencyValue,
        },
        tributeTotals: {
          totalInBRL: serviceTaxData.tribute.valueBRL,
          totalInCurrency: serviceTaxData.tribute.currencyValue,
        },
        supplierTributeTotals: {
          totalInBRL: supplierTributeTaxData.valueBRL,
          totalInCurrency: supplierTributeTaxData.currencyValue,
        },
        extraTotals: {
          totalInBRL: 0,
          totalInCurrency: 0,
        },
        totals: service[key].totals,
      });
    }

    // soma valor do serviço ao valor total de tipo brl
    emptyTotal.byCalculationType[service.calcOption].SUM = {
      taxes: {
        totalInBRL:
          emptyTotal.byCalculationType[service.calcOption].SUM.taxes
            .totalInBRL + serviceTaxesBRL,
        totalInCurrency:
          emptyTotal.byCalculationType[service.calcOption].SUM.taxes
            .totalInCurrency + serviceTaxesCurrency,
      },
      revenueTotals: {
        totalInBRL:
          emptyTotal.byCalculationType[service.calcOption].SUM.revenueTotals
            .totalInBRL + serviceRevenueBRL,
        totalInCurrency:
          emptyTotal.byCalculationType[service.calcOption].SUM.revenueTotals
            .totalInCurrency + serviceRevenueConverted,
      },
      serviceTaxTotals: {
        totalInBRL:
          emptyTotal.byCalculationType[service.calcOption].SUM.serviceTaxTotals
            .totalInBRL + serviceTaxData.serviceTax.valueBRL,
        totalInCurrency:
          emptyTotal.byCalculationType[service.calcOption].SUM.serviceTaxTotals
            .totalInCurrency + serviceTaxData.serviceTax.currencyValue,
      },
      tariffTotals: {
        totalInBRL:
          emptyTotal.byCalculationType[service.calcOption].SUM.tariffTotals
            .totalInBRL + serviceTaxData.tariff.valueBRL,
        totalInCurrency:
          emptyTotal.byCalculationType[service.calcOption].SUM.tariffTotals
            .totalInCurrency + serviceTaxData.tariff.currencyValue,
      },
      doubleChargedTotals: {
        totalInBRL:
          emptyTotal.byCalculationType[service.calcOption].SUM
            .doubleChargedTotals.totalInBRL +
          serviceTaxData.doubleCharged.valueBRL,
        totalInCurrency:
          emptyTotal.byCalculationType[service.calcOption].SUM
            .doubleChargedTotals.totalInCurrency +
          serviceTaxData.doubleCharged.currencyValue,
      },
      localTaxTotals: {
        totalInBRL:
          emptyTotal.byCalculationType[service.calcOption].SUM.localTaxTotals
            .totalInBRL + serviceTaxData.localTax.valueBRL,
        totalInCurrency:
          emptyTotal.byCalculationType[service.calcOption].SUM.localTaxTotals
            .totalInCurrency + serviceTaxData.localTax.currencyValue,
      },
      tourismTaxTotals: {
        totalInBRL:
          emptyTotal.byCalculationType[service.calcOption].SUM.tourismTaxTotals
            .totalInBRL + serviceTaxData.tourismTax.valueBRL,
        totalInCurrency:
          emptyTotal.byCalculationType[service.calcOption].SUM.tourismTaxTotals
            .totalInCurrency + serviceTaxData.tourismTax.currencyValue,
      },
      iofTotals: {
        totalInBRL:
          emptyTotal.byCalculationType[service.calcOption].SUM.iofTotals
            .totalInBRL + serviceTaxData.iof.valueBRL,
        totalInCurrency:
          emptyTotal.byCalculationType[service.calcOption].SUM.iofTotals
            .totalInCurrency + serviceTaxData.iof.currencyValue,
      },
      feeTotals: {
        totalInBRL:
          emptyTotal.byCalculationType[service.calcOption].SUM.feeTotals
            .totalInBRL + serviceTaxData.fee.valueBRL,
        totalInCurrency:
          emptyTotal.byCalculationType[service.calcOption].SUM.feeTotals
            .totalInCurrency + serviceTaxData.fee.currencyValue,
      },
      issTotals: {
        totalInBRL:
          emptyTotal.byCalculationType[service.calcOption].SUM.issTotals
            .totalInBRL + serviceTaxData.iss.valueBRL,
        totalInCurrency:
          emptyTotal.byCalculationType[service.calcOption].SUM.issTotals
            .totalInCurrency + serviceTaxData.iss.currencyValue,
      },
      cbsTotals: {
        totalInBRL:
          (emptyTotal.byCalculationType[service.calcOption].SUM.cbsTotals
            ?.totalInBRL ?? 0) + (serviceTaxData.cbs?.valueBRL ?? 0),
        totalInCurrency:
          (emptyTotal.byCalculationType[service.calcOption].SUM.cbsTotals
            ?.totalInCurrency ?? 0) + (serviceTaxData.cbs?.currencyValue ?? 0),
      },
      ibsTotals: {
        totalInBRL:
          (emptyTotal.byCalculationType[service.calcOption].SUM.ibsTotals
            ?.totalInBRL ?? 0) + (serviceTaxData.ibs?.valueBRL ?? 0),
        totalInCurrency:
          (emptyTotal.byCalculationType[service.calcOption].SUM.ibsTotals
            ?.totalInCurrency ?? 0) + (serviceTaxData.ibs?.currencyValue ?? 0),
      },
      overheadTotals: {
        totalInBRL:
          emptyTotal.byCalculationType[service.calcOption].SUM.overheadTotals
            .totalInBRL + serviceTaxData.overhead.valueBRL,
        totalInCurrency:
          emptyTotal.byCalculationType[service.calcOption].SUM.overheadTotals
            .totalInCurrency + serviceTaxData.overhead.currencyValue,
      },
      markupTotals: {
        totalInBRL:
          emptyTotal.byCalculationType[service.calcOption].SUM.markupTotals
            .totalInBRL + serviceTaxData.markup.valueBRL,
        totalInCurrency:
          emptyTotal.byCalculationType[service.calcOption].SUM.markupTotals
            .totalInCurrency + serviceTaxData.markup.currencyValue,
      },
      commissionTotals: {
        totalInBRL:
          emptyTotal.byCalculationType[service.calcOption].SUM.commissionTotals
            .totalInBRL + serviceTaxData.commission.valueBRL,
        totalInCurrency:
          emptyTotal.byCalculationType[service.calcOption].SUM.commissionTotals
            .totalInCurrency + serviceTaxData.commission.currencyValue,
      },
      incentiveTotals: {
        totalInBRL:
          emptyTotal.byCalculationType[service.calcOption].SUM.incentiveTotals
            .totalInBRL + serviceTaxData.incentive.valueBRL,
        totalInCurrency:
          emptyTotal.byCalculationType[service.calcOption].SUM.incentiveTotals
            .totalInCurrency + serviceTaxData.incentive.currencyValue,
      },
      tributeTotals: {
        totalInBRL:
          emptyTotal.byCalculationType[service.calcOption].SUM.tributeTotals
            .totalInBRL + serviceTaxData.tribute.valueBRL,
        totalInCurrency:
          emptyTotal.byCalculationType[service.calcOption].SUM.tributeTotals
            .totalInCurrency + serviceTaxData.tribute.currencyValue,
      },
      supplierTributeTotals: {
        totalInBRL:
          emptyTotal.byCalculationType[service.calcOption].SUM
            .supplierTributeTotals.totalInBRL + supplierTributeTaxData.valueBRL,
        totalInCurrency:
          emptyTotal.byCalculationType[service.calcOption].SUM
            .supplierTributeTotals.totalInCurrency +
          supplierTributeTaxData.currencyValue,
      },
      totals: {
        totalInBRL:
          emptyTotal.byCalculationType[service.calcOption].SUM.totals
            .totalInBRL + service[key].totals.totalInBRL,
        totalInCurrency:
          emptyTotal.byCalculationType[service.calcOption].SUM.totals
            .totalInCurrency + service[key].totals.totalInCurrency,
      },
      extraTotals: {
        totalInBRL: 0,
        totalInCurrency: 0,
      },
    };

    emptyTotal.byItemType[service.type as BudgetItemType].totalInBRL +=
      service[key].totals.totalInBRL;
    // soma valor do serviço ao valor total de tipo moeda escolhida
    emptyTotal.byItemType[service.type as BudgetItemType].totalInCurrency +=
      service[key].totals.totalInCurrency;
    // agrega a soma total
    emptyTotal.byItemType["SUM"].totalInBRL += service[key].totals.totalInBRL;
    // soma valor do serviço ao valor total de tipo moeda escolhida
    emptyTotal.byItemType["SUM"].totalInCurrency +=
      service[key].totals.totalInCurrency;
  }

  // Aggregate all calc types into the general SUM
  const calcTypesToAggregate: CalcOption[] = ["A", "B", "C", "D"];
  const generalSum = (emptyTotal.byCalculationType as any).SUM as TotalByType;

  for (const calcType of calcTypesToAggregate) {
    const calcTypeSum = emptyTotal.byCalculationType[calcType]
      .SUM as TotalSplited;

    const currentSum = generalSum.SUM as TotalSplited;

    generalSum.SUM = {
      extraTotals: { totalInBRL: 0, totalInCurrency: 0 },
      taxes: {
        totalInBRL: currentSum.taxes.totalInBRL + calcTypeSum.taxes.totalInBRL,
        totalInCurrency:
          currentSum.taxes.totalInCurrency + calcTypeSum.taxes.totalInCurrency,
      },
      revenueTotals: {
        totalInBRL:
          currentSum.revenueTotals.totalInBRL +
          calcTypeSum.revenueTotals.totalInBRL,
        totalInCurrency:
          currentSum.revenueTotals.totalInCurrency +
          calcTypeSum.revenueTotals.totalInCurrency,
      },
      serviceTaxTotals: {
        totalInBRL:
          currentSum.serviceTaxTotals.totalInBRL +
          calcTypeSum.serviceTaxTotals.totalInBRL,
        totalInCurrency:
          currentSum.serviceTaxTotals.totalInCurrency +
          calcTypeSum.serviceTaxTotals.totalInCurrency,
      },
      tariffTotals: {
        totalInBRL:
          currentSum.tariffTotals.totalInBRL +
          calcTypeSum.tariffTotals.totalInBRL,
        totalInCurrency:
          currentSum.tariffTotals.totalInCurrency +
          calcTypeSum.tariffTotals.totalInCurrency,
      },
      doubleChargedTotals: {
        totalInBRL:
          currentSum.doubleChargedTotals.totalInBRL +
          calcTypeSum.doubleChargedTotals.totalInBRL,
        totalInCurrency:
          currentSum.doubleChargedTotals.totalInCurrency +
          calcTypeSum.doubleChargedTotals.totalInCurrency,
      },
      localTaxTotals: {
        totalInBRL:
          currentSum.localTaxTotals.totalInBRL +
          calcTypeSum.localTaxTotals.totalInBRL,
        totalInCurrency:
          currentSum.localTaxTotals.totalInCurrency +
          calcTypeSum.localTaxTotals.totalInCurrency,
      },
      tourismTaxTotals: {
        totalInBRL:
          currentSum.tourismTaxTotals.totalInBRL +
          calcTypeSum.tourismTaxTotals.totalInBRL,
        totalInCurrency:
          currentSum.tourismTaxTotals.totalInCurrency +
          calcTypeSum.tourismTaxTotals.totalInCurrency,
      },
      iofTotals: {
        totalInBRL:
          currentSum.iofTotals.totalInBRL + calcTypeSum.iofTotals.totalInBRL,
        totalInCurrency:
          currentSum.iofTotals.totalInCurrency +
          calcTypeSum.iofTotals.totalInCurrency,
      },
      feeTotals: {
        totalInBRL:
          currentSum.feeTotals.totalInBRL + calcTypeSum.feeTotals.totalInBRL,
        totalInCurrency:
          currentSum.feeTotals.totalInCurrency +
          calcTypeSum.feeTotals.totalInCurrency,
      },
      issTotals: {
        totalInBRL:
          currentSum.issTotals.totalInBRL + calcTypeSum.issTotals.totalInBRL,
        totalInCurrency:
          currentSum.issTotals.totalInCurrency +
          calcTypeSum.issTotals.totalInCurrency,
      },
      cbsTotals: {
        totalInBRL:
          (currentSum.cbsTotals?.totalInBRL ?? 0) +
          (calcTypeSum.cbsTotals?.totalInBRL ?? 0),
        totalInCurrency:
          (currentSum.cbsTotals?.totalInCurrency ?? 0) +
          (calcTypeSum.cbsTotals?.totalInCurrency ?? 0),
      },
      ibsTotals: {
        totalInBRL:
          (currentSum.ibsTotals?.totalInBRL ?? 0) +
          (calcTypeSum.ibsTotals?.totalInBRL ?? 0),
        totalInCurrency:
          (currentSum.ibsTotals?.totalInCurrency ?? 0) +
          (calcTypeSum.ibsTotals?.totalInCurrency ?? 0),
      },
      overheadTotals: {
        totalInBRL:
          currentSum.overheadTotals.totalInBRL +
          calcTypeSum.overheadTotals.totalInBRL,
        totalInCurrency:
          currentSum.overheadTotals.totalInCurrency +
          calcTypeSum.overheadTotals.totalInCurrency,
      },
      markupTotals: {
        totalInBRL:
          currentSum.markupTotals.totalInBRL +
          calcTypeSum.markupTotals.totalInBRL,
        totalInCurrency:
          currentSum.markupTotals.totalInCurrency +
          calcTypeSum.markupTotals.totalInCurrency,
      },
      commissionTotals: {
        totalInBRL:
          currentSum.commissionTotals.totalInBRL +
          calcTypeSum.commissionTotals.totalInBRL,
        totalInCurrency:
          currentSum.commissionTotals.totalInCurrency +
          calcTypeSum.commissionTotals.totalInCurrency,
      },
      incentiveTotals: {
        totalInBRL:
          currentSum.incentiveTotals.totalInBRL +
          calcTypeSum.incentiveTotals.totalInBRL,
        totalInCurrency:
          currentSum.incentiveTotals.totalInCurrency +
          calcTypeSum.incentiveTotals.totalInCurrency,
      },
      tributeTotals: {
        totalInBRL:
          currentSum.tributeTotals.totalInBRL +
          calcTypeSum.tributeTotals.totalInBRL,
        totalInCurrency:
          currentSum.tributeTotals.totalInCurrency +
          calcTypeSum.tributeTotals.totalInCurrency,
      },
      supplierTributeTotals: {
        totalInBRL:
          currentSum.supplierTributeTotals.totalInBRL +
          calcTypeSum.supplierTributeTotals.totalInBRL,
        totalInCurrency:
          currentSum.supplierTributeTotals.totalInCurrency +
          calcTypeSum.supplierTributeTotals.totalInCurrency,
      },
      totals: {
        totalInBRL:
          currentSum.totals.totalInBRL + calcTypeSum.totals.totalInBRL,
        totalInCurrency:
          currentSum.totals.totalInCurrency +
          calcTypeSum.totals.totalInCurrency,
      },
    } as TotalSplited;
  }
  if (extras.typeTax === "PERCENTAGE") {
    generalSum.SUM.extraTotals.totalInBRL =
      generalSum.SUM.totals.totalInBRL * (extras.value / 100);
    generalSum.SUM.extraTotals.totalInCurrency =
      generalSum.SUM.totals.totalInCurrency * (extras.value / 100);
  } else {
    generalSum.SUM.extraTotals.totalInBRL = extras.value;
    generalSum.SUM.extraTotals.totalInCurrency = extras.value;
  }

  return emptyTotal;
}

export function calculateDetailedTotalsNegotiation(
  budgetServices: ServiceData[],
  versionInfo: versionInfo,
  extras: DefaultTax
): VersionTotals {
  const emptyTotal = createEmptyVersionTotals();

  for (const service of budgetServices) {
    // Mesma regra da cópia em BudgetContext/calc-methods.ts: o serviço excluído numa
    // etapa versionada sai de `service.versions` mas mantém o valor gravado em
    // `negotiationValue[version-1]`, então quem soma precisa filtrar por versão — senão
    // o total da negociação N ainda inclui o item removido e o savings atrasa uma versão.
    // O par desta regra para o fechamento já existe em ServicesContext/reducer-function.ts
    // (MMV-2419-10); esta é a metade que faltava, da negociação.
    //
    // Neste fluxo (pré-orçamento/Serviços) o reducer não escreve `versions`: por isso o
    // teste é `service.versions &&` — sem o campo, o serviço conta em todas as versões e
    // o comportamento atual fica preservado.
    if (service.versions && !service.versions.includes(versionInfo.version))
      continue;
    // valida se já tem um supplier com valores
    const supplierIndex = emptyTotal.byCalculationType[service.calcOption][
      service.type as BudgetItemType
    ].findIndex((el) => el.supplierId === service.supplierId);
    const valuesVersion = service.negotiationValue[versionInfo.version - 1];
    // Serviço sem slot gravado para esta versão (ex.: criado depois da negociação):
    // sem a guarda, `valuesVersion.taxData` lança e todo o cálculo da versão aborta.
    if (!valuesVersion) continue;
    const serviceTaxData = valuesVersion.taxData;
    const supplierTributeTaxData =
      serviceTaxData.supplier_tribute ?? serviceValues.taxData.supplier_tribute;
    const serviceRevenueBRL =
      serviceTaxData.fee.valueBRL +
      serviceTaxData.markup.valueBRL +
      serviceTaxData.overhead.valueBRL +
      serviceTaxData.commission.valueBRL +
      serviceTaxData.incentive.valueBRL;

    const serviceTaxesCurrency =
      serviceTaxData.iss.currencyValue +
      serviceTaxData.tribute.currencyValue +
      serviceTaxData.localTax.currencyValue;

    const serviceTaxesBRL =
      serviceTaxData.iss.valueBRL +
      serviceTaxData.tribute.valueBRL +
      serviceTaxData.localTax.valueBRL;

    const serviceRevenueConverted =
      serviceTaxData.fee.currencyValue +
      serviceTaxData.markup.currencyValue +
      serviceTaxData.overhead.currencyValue +
      serviceTaxData.commission.currencyValue +
      serviceTaxData.incentive.currencyValue;

    // adiciona instancia total splited em byCalcType
    //se existe index para o fornecedor em questão soma os valores
    if (supplierIndex > -1) {
      const supplierTotal =
        emptyTotal.byCalculationType[service.calcOption][
          service.type as BudgetItemType
        ][supplierIndex];

      emptyTotal.byCalculationType[service.calcOption][
        service.type as BudgetItemType
      ][supplierIndex] = {
        ...supplierTotal,
        totals: {
          totalInBRL:
            supplierTotal.totals.totalInBRL + valuesVersion.totals.totalInBRL,
          totalInCurrency:
            supplierTotal.totals.totalInCurrency +
            valuesVersion.totals.totalInCurrency,
        },
        taxes: {
          totalInBRL: supplierTotal.taxes.totalInBRL + serviceTaxesBRL,
          totalInCurrency:
            supplierTotal.taxes.totalInCurrency + serviceTaxesCurrency,
        },
        tariffTotals: {
          totalInBRL:
            supplierTotal.tariffTotals.totalInBRL +
            serviceTaxData.tariff.valueBRL,
          totalInCurrency:
            supplierTotal.tariffTotals.totalInCurrency +
            serviceTaxData.tariff.valueBRL,
        },
        revenueTotals: {
          totalInBRL:
            supplierTotal.revenueTotals.totalInBRL + serviceRevenueBRL,
          totalInCurrency:
            supplierTotal.revenueTotals.totalInCurrency +
            serviceRevenueConverted,
        },
        serviceTaxTotals: {
          totalInBRL:
            supplierTotal.serviceTaxTotals.totalInBRL +
            serviceTaxData.serviceTax.valueBRL,
          totalInCurrency:
            supplierTotal.serviceTaxTotals.totalInCurrency +
            serviceTaxData.serviceTax.currencyValue,
        },
        localTaxTotals: {
          totalInBRL:
            supplierTotal.localTaxTotals.totalInBRL +
            serviceTaxData.localTax.valueBRL,
          totalInCurrency:
            supplierTotal.localTaxTotals.totalInCurrency +
            serviceTaxData.localTax.currencyValue,
        },
        tourismTaxTotals: {
          totalInBRL:
            supplierTotal.tourismTaxTotals.totalInBRL +
            serviceTaxData.tourismTax.valueBRL,
          totalInCurrency:
            supplierTotal.tourismTaxTotals.totalInCurrency +
            serviceTaxData.tourismTax.currencyValue,
        },
        iofTotals: {
          totalInBRL:
            supplierTotal.iofTotals.totalInBRL + serviceTaxData.iof.valueBRL,
          totalInCurrency:
            supplierTotal.iofTotals.totalInCurrency +
            serviceTaxData.iof.currencyValue,
        },
        feeTotals: {
          totalInBRL:
            supplierTotal.feeTotals.totalInBRL + serviceTaxData.fee.valueBRL,
          totalInCurrency:
            supplierTotal.feeTotals.totalInCurrency +
            serviceTaxData.fee.currencyValue,
        },
        issTotals: {
          totalInBRL:
            supplierTotal.issTotals.totalInBRL + serviceTaxData.iss.valueBRL,
          totalInCurrency:
            supplierTotal.issTotals.totalInCurrency +
            serviceTaxData.iss.currencyValue,
        },
        cbsTotals: {
          totalInBRL:
            (supplierTotal.cbsTotals?.totalInBRL ?? 0) +
            (serviceTaxData.cbs?.valueBRL ?? 0),
          totalInCurrency:
            (supplierTotal.cbsTotals?.totalInCurrency ?? 0) +
            (serviceTaxData.cbs?.currencyValue ?? 0),
        },
        ibsTotals: {
          totalInBRL:
            (supplierTotal.ibsTotals?.totalInBRL ?? 0) +
            (serviceTaxData.ibs?.valueBRL ?? 0),
          totalInCurrency:
            (supplierTotal.ibsTotals?.totalInCurrency ?? 0) +
            (serviceTaxData.ibs?.currencyValue ?? 0),
        },
        overheadTotals: {
          totalInBRL:
            supplierTotal.overheadTotals.totalInBRL +
            serviceTaxData.overhead.valueBRL,
          totalInCurrency:
            supplierTotal.overheadTotals.totalInCurrency +
            serviceTaxData.overhead.currencyValue,
        },
        markupTotals: {
          totalInBRL:
            supplierTotal.markupTotals.totalInBRL +
            serviceTaxData.markup.valueBRL,
          totalInCurrency:
            supplierTotal.markupTotals.totalInCurrency +
            serviceTaxData.markup.currencyValue,
        },
        commissionTotals: {
          totalInBRL:
            supplierTotal.commissionTotals.totalInBRL +
            serviceTaxData.commission.valueBRL,
          totalInCurrency:
            supplierTotal.commissionTotals.totalInCurrency +
            serviceTaxData.commission.currencyValue,
        },
        incentiveTotals: {
          totalInBRL:
            supplierTotal.incentiveTotals.totalInBRL +
            serviceTaxData.incentive.valueBRL,
          totalInCurrency:
            supplierTotal.incentiveTotals.totalInCurrency +
            serviceTaxData.incentive.currencyValue,
        },
        tributeTotals: {
          totalInBRL:
            supplierTotal.tributeTotals.totalInBRL +
            serviceTaxData.tribute.valueBRL,
          totalInCurrency:
            supplierTotal.tributeTotals.totalInCurrency +
            serviceTaxData.tribute.currencyValue,
        },
        supplierTributeTotals: {
          totalInBRL:
            supplierTotal.supplierTributeTotals.totalInBRL +
            supplierTributeTaxData.valueBRL,
          totalInCurrency:
            supplierTotal.supplierTributeTotals.totalInCurrency +
            supplierTributeTaxData.currencyValue,
        },
      };
    } else {
      emptyTotal.byCalculationType[service.calcOption][
        service.type as BudgetItemType
      ].push({
        supplierId: service.supplierId,
        calcOption: service.calcOption,
        taxes: {
          totalInBRL: serviceTaxesBRL,
          totalInCurrency: serviceTaxesCurrency,
        },
        revenueTotals: {
          totalInBRL: serviceRevenueBRL,
          totalInCurrency: serviceRevenueConverted,
        },
        serviceTaxTotals: {
          totalInBRL: serviceTaxData.serviceTax.valueBRL,
          totalInCurrency: serviceTaxData.serviceTax.currencyValue,
        },
        doubleChargedTotals: {
          totalInBRL: serviceTaxData.doubleCharged.valueBRL,
          totalInCurrency: serviceTaxData.doubleCharged.currencyValue,
        },
        tariffTotals: {
          totalInBRL: serviceTaxData.tariff.valueBRL,
          totalInCurrency: serviceTaxData.tariff.currencyValue,
        },
        localTaxTotals: {
          totalInBRL: serviceTaxData.localTax.valueBRL,
          totalInCurrency: serviceTaxData.localTax.currencyValue,
        },
        tourismTaxTotals: {
          totalInBRL: serviceTaxData.tourismTax.valueBRL,
          totalInCurrency: serviceTaxData.tourismTax.currencyValue,
        },
        iofTotals: {
          totalInBRL: serviceTaxData.iof.valueBRL,
          totalInCurrency: serviceTaxData.iof.currencyValue,
        },
        feeTotals: {
          totalInBRL: serviceTaxData.fee.valueBRL,
          totalInCurrency: serviceTaxData.fee.currencyValue,
        },
        issTotals: {
          totalInBRL: serviceTaxData.iss.valueBRL,
          totalInCurrency: serviceTaxData.iss.currencyValue,
        },
        cbsTotals: {
          totalInBRL: serviceTaxData.cbs?.valueBRL ?? 0,
          totalInCurrency: serviceTaxData.cbs?.currencyValue ?? 0,
        },
        ibsTotals: {
          totalInBRL: serviceTaxData.ibs?.valueBRL ?? 0,
          totalInCurrency: serviceTaxData.ibs?.currencyValue ?? 0,
        },
        overheadTotals: {
          totalInBRL: serviceTaxData.overhead.valueBRL,
          totalInCurrency: serviceTaxData.overhead.currencyValue,
        },
        markupTotals: {
          totalInBRL: serviceTaxData.markup.valueBRL,
          totalInCurrency: serviceTaxData.markup.currencyValue,
        },
        commissionTotals: {
          totalInBRL: serviceTaxData.commission.valueBRL,
          totalInCurrency: serviceTaxData.commission.currencyValue,
        },
        incentiveTotals: {
          totalInBRL: serviceTaxData.incentive.valueBRL,
          totalInCurrency: serviceTaxData.incentive.currencyValue,
        },
        tributeTotals: {
          totalInBRL: serviceTaxData.tribute.valueBRL,
          totalInCurrency: serviceTaxData.tribute.currencyValue,
        },
        supplierTributeTotals: {
          totalInBRL: supplierTributeTaxData.valueBRL,
          totalInCurrency: supplierTributeTaxData.currencyValue,
        },
        totals: valuesVersion.totals,
        extraTotals: { totalInBRL: 0, totalInCurrency: 0 },
      });
    }

    // soma valor do serviço ao valor total de tipo brl
    emptyTotal.byCalculationType[service.calcOption].SUM = {
      taxes: {
        totalInBRL:
          emptyTotal.byCalculationType[service.calcOption].SUM.taxes
            .totalInBRL + serviceTaxesBRL,
        totalInCurrency:
          emptyTotal.byCalculationType[service.calcOption].SUM.taxes
            .totalInCurrency + serviceTaxesCurrency,
      },
      revenueTotals: {
        totalInBRL:
          emptyTotal.byCalculationType[service.calcOption].SUM.revenueTotals
            .totalInBRL + serviceRevenueBRL,
        totalInCurrency:
          emptyTotal.byCalculationType[service.calcOption].SUM.revenueTotals
            .totalInCurrency + serviceRevenueConverted,
      },
      serviceTaxTotals: {
        totalInBRL:
          emptyTotal.byCalculationType[service.calcOption].SUM.serviceTaxTotals
            .totalInBRL + serviceTaxData.serviceTax.valueBRL,
        totalInCurrency:
          emptyTotal.byCalculationType[service.calcOption].SUM.serviceTaxTotals
            .totalInCurrency + serviceTaxData.serviceTax.currencyValue,
      },
      doubleChargedTotals: {
        totalInBRL:
          emptyTotal.byCalculationType[service.calcOption].SUM
            .doubleChargedTotals.totalInBRL +
          serviceTaxData.serviceTax.valueBRL,
        totalInCurrency:
          emptyTotal.byCalculationType[service.calcOption].SUM
            .doubleChargedTotals.totalInCurrency +
          serviceTaxData.serviceTax.currencyValue,
      },
      tariffTotals: {
        totalInBRL:
          emptyTotal.byCalculationType[service.calcOption].SUM.tariffTotals
            .totalInBRL + serviceTaxData.tariff.valueBRL,
        totalInCurrency:
          emptyTotal.byCalculationType[service.calcOption].SUM.tariffTotals
            .totalInCurrency + serviceTaxData.tariff.currencyValue,
      },
      localTaxTotals: {
        totalInBRL:
          emptyTotal.byCalculationType[service.calcOption].SUM.localTaxTotals
            .totalInBRL + serviceTaxData.localTax.valueBRL,
        totalInCurrency:
          emptyTotal.byCalculationType[service.calcOption].SUM.localTaxTotals
            .totalInCurrency + serviceTaxData.localTax.currencyValue,
      },
      tourismTaxTotals: {
        totalInBRL:
          emptyTotal.byCalculationType[service.calcOption].SUM.tourismTaxTotals
            .totalInBRL + serviceTaxData.tourismTax.valueBRL,
        totalInCurrency:
          emptyTotal.byCalculationType[service.calcOption].SUM.tourismTaxTotals
            .totalInCurrency + serviceTaxData.tourismTax.currencyValue,
      },
      iofTotals: {
        totalInBRL:
          emptyTotal.byCalculationType[service.calcOption].SUM.iofTotals
            .totalInBRL + serviceTaxData.iof.valueBRL,
        totalInCurrency:
          emptyTotal.byCalculationType[service.calcOption].SUM.iofTotals
            .totalInCurrency + serviceTaxData.iof.currencyValue,
      },
      feeTotals: {
        totalInBRL:
          emptyTotal.byCalculationType[service.calcOption].SUM.feeTotals
            .totalInBRL + serviceTaxData.fee.valueBRL,
        totalInCurrency:
          emptyTotal.byCalculationType[service.calcOption].SUM.feeTotals
            .totalInCurrency + serviceTaxData.fee.currencyValue,
      },
      issTotals: {
        totalInBRL:
          emptyTotal.byCalculationType[service.calcOption].SUM.issTotals
            .totalInBRL + serviceTaxData.iss.valueBRL,
        totalInCurrency:
          emptyTotal.byCalculationType[service.calcOption].SUM.issTotals
            .totalInCurrency + serviceTaxData.iss.currencyValue,
      },
      cbsTotals: {
        totalInBRL:
          (emptyTotal.byCalculationType[service.calcOption].SUM.cbsTotals
            ?.totalInBRL ?? 0) + (serviceTaxData.cbs?.valueBRL ?? 0),
        totalInCurrency:
          (emptyTotal.byCalculationType[service.calcOption].SUM.cbsTotals
            ?.totalInCurrency ?? 0) + (serviceTaxData.cbs?.currencyValue ?? 0),
      },
      ibsTotals: {
        totalInBRL:
          (emptyTotal.byCalculationType[service.calcOption].SUM.ibsTotals
            ?.totalInBRL ?? 0) + (serviceTaxData.ibs?.valueBRL ?? 0),
        totalInCurrency:
          (emptyTotal.byCalculationType[service.calcOption].SUM.ibsTotals
            ?.totalInCurrency ?? 0) + (serviceTaxData.ibs?.currencyValue ?? 0),
      },
      overheadTotals: {
        totalInBRL:
          emptyTotal.byCalculationType[service.calcOption].SUM.overheadTotals
            .totalInBRL + serviceTaxData.overhead.valueBRL,
        totalInCurrency:
          emptyTotal.byCalculationType[service.calcOption].SUM.overheadTotals
            .totalInCurrency + serviceTaxData.overhead.currencyValue,
      },
      markupTotals: {
        totalInBRL:
          emptyTotal.byCalculationType[service.calcOption].SUM.markupTotals
            .totalInBRL + serviceTaxData.markup.valueBRL,
        totalInCurrency:
          emptyTotal.byCalculationType[service.calcOption].SUM.markupTotals
            .totalInCurrency + serviceTaxData.markup.currencyValue,
      },
      commissionTotals: {
        totalInBRL:
          emptyTotal.byCalculationType[service.calcOption].SUM.commissionTotals
            .totalInBRL + serviceTaxData.commission.valueBRL,
        totalInCurrency:
          emptyTotal.byCalculationType[service.calcOption].SUM.commissionTotals
            .totalInCurrency + serviceTaxData.commission.currencyValue,
      },
      incentiveTotals: {
        totalInBRL:
          emptyTotal.byCalculationType[service.calcOption].SUM.incentiveTotals
            .totalInBRL + serviceTaxData.incentive.valueBRL,
        totalInCurrency:
          emptyTotal.byCalculationType[service.calcOption].SUM.incentiveTotals
            .totalInCurrency + serviceTaxData.incentive.currencyValue,
      },
      tributeTotals: {
        totalInBRL:
          emptyTotal.byCalculationType[service.calcOption].SUM.tributeTotals
            .totalInBRL + serviceTaxData.tribute.valueBRL,
        totalInCurrency:
          emptyTotal.byCalculationType[service.calcOption].SUM.tributeTotals
            .totalInCurrency + serviceTaxData.tribute.currencyValue,
      },
      supplierTributeTotals: {
        totalInBRL:
          emptyTotal.byCalculationType[service.calcOption].SUM
            .supplierTributeTotals.totalInBRL + supplierTributeTaxData.valueBRL,
        totalInCurrency:
          emptyTotal.byCalculationType[service.calcOption].SUM
            .supplierTributeTotals.totalInCurrency +
          supplierTributeTaxData.currencyValue,
      },
      totals: {
        totalInBRL:
          emptyTotal.byCalculationType[service.calcOption].SUM.totals
            .totalInBRL + valuesVersion.totals.totalInBRL,
        totalInCurrency:
          emptyTotal.byCalculationType[service.calcOption].SUM.totals
            .totalInCurrency + valuesVersion.totals.totalInCurrency,
      },
      extraTotals: { totalInBRL: 0, totalInCurrency: 0 },
    };

    emptyTotal.byItemType[service.type as BudgetItemType].totalInBRL +=
      valuesVersion.totals.totalInBRL;
    // soma valor do serviço ao valor total de tipo moeda escolhida
    emptyTotal.byItemType[service.type as BudgetItemType].totalInCurrency +=
      valuesVersion.totals.totalInCurrency;
    // agrega a soma total
    emptyTotal.byItemType["SUM"].totalInBRL += valuesVersion.totals.totalInBRL;
    // soma valor do serviço ao valor total de tipo moeda escolhida
    emptyTotal.byItemType["SUM"].totalInCurrency +=
      valuesVersion.totals.totalInCurrency;
  }
  return emptyTotal;
}

export function calculateNegotiationTotals(budget: Budget) {
  // passa a receber todo o budget
  // cada version em version history gera um total a partir dos valores de serviços dessa versão
  const returnTotals = [];
  for (let i = 0; i < budget.versionHistory.length - 1; i++) {
    const emptyTotal = createEmptyVersionTotals();

    for (const service of budget.budgetServices) {
      // adiciona instancia total splited em byCalcType
      emptyTotal.byCalculationType[service.calcOption][
        service.type as BudgetItemType
      ].push({
        supplierId: service.supplierId,
        calcOption: service.calcOption,
        taxes: {
          totalInBRL: 0,
          totalInCurrency: 0,
        },
        totals: service.negotiationValue[i]?.totals || {
          totalInBRL: 0,
          totalInCurrency: 0,
        },
        revenueTotals: {
          totalInBRL: 0,
          totalInCurrency: 0,
        },
        doubleChargedTotals: {
          totalInBRL: 0,
          totalInCurrency: 0,
        },
        serviceTaxTotals: {
          totalInBRL: 0,
          totalInCurrency: 0,
        },
        tariffTotals: {
          totalInBRL: 0,
          totalInCurrency: 0,
        },
        localTaxTotals: {
          totalInBRL: 0,
          totalInCurrency: 0,
        },
        tourismTaxTotals: {
          totalInBRL: 0,
          totalInCurrency: 0,
        },
        iofTotals: {
          totalInBRL: 0,
          totalInCurrency: 0,
        },
        feeTotals: {
          totalInBRL: 0,
          totalInCurrency: 0,
        },
        issTotals: {
          totalInBRL: 0,
          totalInCurrency: 0,
        },
        cbsTotals: {
          totalInBRL: 0,
          totalInCurrency: 0,
        },
        ibsTotals: {
          totalInBRL: 0,
          totalInCurrency: 0,
        },
        overheadTotals: {
          totalInBRL: 0,
          totalInCurrency: 0,
        },
        markupTotals: {
          totalInBRL: 0,
          totalInCurrency: 0,
        },
        commissionTotals: {
          totalInBRL: 0,
          totalInCurrency: 0,
        },
        incentiveTotals: {
          totalInBRL: 0,
          totalInCurrency: 0,
        },
        tributeTotals: {
          totalInBRL: 0,
          totalInCurrency: 0,
        },
        supplierTributeTotals: {
          totalInBRL: 0,
          totalInCurrency: 0,
        },
        extraTotals: { totalInBRL: 0, totalInCurrency: 0 },
      });
      // soma valor do serviço ao valor total de tipo brl
      emptyTotal.byItemType[service.type as BudgetItemType].totalInBRL +=
        service.negotiationValue[i]?.totals.totalInBRL || 0;
      // soma valor do serviço ao valor total de tipo moeda escolhida
      emptyTotal.byItemType[service.type as BudgetItemType].totalInCurrency +=
        service.negotiationValue[i]?.totals.totalInCurrency || 0;
      // agrega a soma total
      emptyTotal.byItemType["SUM"].totalInBRL +=
        service.negotiationValue[i]?.totals.totalInBRL || 0;
      // soma valor do serviço ao valor total de tipo moeda escolhida
      emptyTotal.byItemType["SUM"].totalInCurrency +=
        service.negotiationValue[i]?.totals.totalInCurrency || 0;
    }
    returnTotals.push(emptyTotal);
  }
  return returnTotals;
}

export interface AllTotals extends TotalByCalcOption {
  budgetTotals: SumsTotalByType;
}
export type TotalByCalcOption = {
  [key in NFMMOption]: TotalByType;
};

export interface ByItemType extends SumsTotalByType {
  SUM: Totals;
}

export type SumsTotalByType = {
  [key in BudgetItemType]: Totals;
} & {
  SUM: Totals;
};

export type Totals = {
  totalInBRL: number;
  totalInCurrency: number;
};
export type CalcOption = "A" | "B" | "C" | "D";

export type BudgetItemType =
  | "AEB"
  | "AIR"
  | "ATTRACTION"
  | "PRODUCTION_SUPPORT"
  | "BRINDES_INCENTIVOS_PREMIACOES"
  | "CENOGRAPHY_DECORATION"
  | "COMMUNICATIONS"
  | "CONTENT"
  | "EQUIPMENTS"
  // | "SUPPORT"
  | "FOTO_VIDEO"
  | "ACCOMMODATIONS"
  | "SUBSCRIPTION"
  | "LICENCES_TAXES"
  | "GRAPHIC_MATERIAL"
  | "MOUNTING_COMPANY"
  | "NECESSARY_ADDITIONS"
  // | "SEVERAL"
  | "HUMAN_RESOURCES_STAFF"
  | "RSVP"
  | "ROOMS"
  | "TRAVEL_INSURANCE"
  | "SERVICE_SUPPORT"
  | "EVENT_INSURANCE"
  | "TECHNOLOGY"
  | "TRANSLATIONS"
  | "TRANSFER"
  | "PARKING";

export type TotalSplited = {
  supplierId?: string;
  calcOption?: CalcOption;
  taxes: Totals;
  totals: Totals;
  revenueTotals: Totals;
  serviceTaxTotals: Totals;
  tariffTotals: Totals;
  localTaxTotals: Totals;
  tourismTaxTotals: Totals;
  iofTotals: Totals;
  feeTotals: Totals;
  issTotals: Totals;
  cbsTotals: Totals;
  ibsTotals: Totals;
  overheadTotals: Totals;
  markupTotals: Totals;
  doubleChargedTotals: Totals;
  commissionTotals: Totals;
  incentiveTotals: Totals;
  tributeTotals: Totals;
  supplierTributeTotals: Totals;
  extraTotals: Totals;
};

export type TaxData = {
  tariff: DefaultTax; // tarifa nominal
  serviceTax: DefaultTax; // taxa de serviço
  localTax: DefaultTax; // imposto municipal
  tourismTax: DefaultTax; // taca de turismo
  iof: DefaultTax; // calculado com base na localidade
  fee: DefaultTax; // honorarios
  iss: DefaultTax; //  calculado com base na localidade
  cbs: DefaultTax;
  ibs: DefaultTax;
  typeNfMm: CalcOption;
  overhead: DefaultTax; // campos de receita
  markup: DefaultTax; // campos de receita
  commission: DefaultTax; // campos de receita
  incentive: DefaultTax; // campos de receita
  tribute: DefaultTax; // imposto // campos de receita
  supplier_tribute: DefaultTax;
  doubleCharged: DefaultTax; // imposto // campos de receita
  extras: DefaultTax; // imposto // campos de receita
};

export interface ExchangeData {
  date: string;
  currency: string;
  value: number;
}

export interface BaseValue {
  version: number;
  supplierId: string;
  category: BudgetItemType;
  calcOption: CalcOption;
  taxData: TaxData;
  exchange: ExchangeData;
  descriptions: {
    composition: string;
    cancelPolicy: string;
    paymentMethod: string;
    commercialConditions: boolean;
  };
}

// ajuste o caminho

const zeroTax: DefaultTax = {
  typeTax: "PERCENTAGE",
  value: 0,
  currencyValue: 0,
  valueBRL: 0,
};

export const baseValue: BaseValue = {
  version: 0,
  supplierId: "",
  category: "" as BudgetItemType, // substitua com 'BudgetItemType.EXEMPLO' se tiver enums definidos
  calcOption: "" as CalcOption, // substitua com 'CalcOption.EXEMPLO' se tiver enums definidos
  exchange: { currency: "BRL", date: "", value: 1 } as ExchangeData, // preencha conforme a estrutura real de ExchangeData
  taxData: {
    tariff: { ...zeroTax },
    serviceTax: { ...zeroTax },
    localTax: { ...zeroTax },
    tourismTax: { ...zeroTax },
    iof: { ...zeroTax },
    fee: { ...zeroTax },
    iss: { ...zeroTax },
    cbs: { ...zeroTax },
    ibs: { ...zeroTax },
    typeNfMm: "" as CalcOption,
    overhead: { ...zeroTax },
    markup: { ...zeroTax },
    commission: { ...zeroTax },
    incentive: { ...zeroTax },
    tribute: { ...zeroTax },
    supplier_tribute: { ...zeroTax },
    doubleCharged: { ...zeroTax },
    extras: { ...zeroTax },
  },
  descriptions: {
    composition: "",
    cancelPolicy: "",
    paymentMethod: "",
    commercialConditions: false,
  },
};

export const createEmptyVersionTotals = (): VersionTotals => {
  // Initialize all totals to zero
  const emptyTotals: Totals = { totalInBRL: 0, totalInCurrency: 0 };

  // Cria um novo objeto de arrays independentes para cada calcOption
  const createIndependentTotalByType = (): TotalByType => {
    return Object.fromEntries(
      [...budgetItemTypes, "SUM"].map((key) =>
        key !== "SUM"
          ? [key, []]
          : [
              key,
              {
                taxes: {
                  totalInBRL: 0,
                  totalInCurrency: 0,
                },
                revenueTotals: {
                  totalInBRL: 0,
                  totalInCurrency: 0,
                },
                serviceTaxTotals: {
                  totalInBRL: 0,
                  totalInCurrency: 0,
                },
                tariffTotals: {
                  totalInBRL: 0,
                  totalInCurrency: 0,
                },
                localTaxTotals: {
                  totalInBRL: 0,
                  totalInCurrency: 0,
                },
                tourismTaxTotals: {
                  totalInBRL: 0,
                  totalInCurrency: 0,
                },
                iofTotals: {
                  totalInBRL: 0,
                  totalInCurrency: 0,
                },
                feeTotals: {
                  totalInBRL: 0,
                  totalInCurrency: 0,
                },
                issTotals: {
                  totalInBRL: 0,
                  totalInCurrency: 0,
                },
                cbsTotals: {
                  totalInBRL: 0,
                  totalInCurrency: 0,
                },
                ibsTotals: {
                  totalInBRL: 0,
                  totalInCurrency: 0,
                },
                overheadTotals: {
                  totalInBRL: 0,
                  totalInCurrency: 0,
                },
                markupTotals: {
                  totalInBRL: 0,
                  totalInCurrency: 0,
                },
                commissionTotals: {
                  totalInBRL: 0,
                  totalInCurrency: 0,
                },
                incentiveTotals: {
                  totalInBRL: 0,
                  totalInCurrency: 0,
                },
                tributeTotals: {
                  totalInBRL: 0,
                  totalInCurrency: 0,
                },
                supplierTributeTotals: {
                  totalInBRL: 0,
                  totalInCurrency: 0,
                },
                doubleChargedTotals: {
                  totalInBRL: 0,
                  totalInCurrency: 0,
                },
                totals: { totalInBRL: 0, totalInCurrency: 0 },
              } as TotalSplited,
            ]
      )
    ) as unknown as TotalByType;
  };

  // Cria um novo objeto de somatórios independentes para cada calcOption
  const createIndependentSumsTotalByType = (): SumsTotalByType => {
    return Object.fromEntries(
      [...budgetItemTypes, "SUM"].map((key) => [key, { ...emptyTotals }])
    ) as SumsTotalByType;
  };

  // Construct the VersionTotals object
  return {
    byCalculationType: {
      ...Object.fromEntries(
        [...calcOptions, "SUM"].map((key) => [
          key,
          createIndependentTotalByType(),
        ])
      ),
      budgetTotals: createIndependentSumsTotalByType(),
    } as AllTotals,
    byItemType: {
      ...createIndependentSumsTotalByType(),
      SUM: { ...emptyTotals },
    } as ByItemType,
  };
};

// Enum values converted to arrays for iteration
const calcOptions: NFMMOption[] = ["A", "B", "C", "D", "SUM"];
const budgetItemTypes: BudgetItemType[] = [
  "AEB",
  "AIR",
  "ATTRACTION",
  "PRODUCTION_SUPPORT",
  "BRINDES_INCENTIVOS_PREMIACOES",
  "CENOGRAPHY_DECORATION",
  "COMMUNICATIONS",
  "CONTENT",
  "EQUIPMENTS",
  "FOTO_VIDEO",
  "ACCOMMODATIONS",
  "SUBSCRIPTION",
  "LICENCES_TAXES",
  "GRAPHIC_MATERIAL",
  "MOUNTING_COMPANY",
  "NECESSARY_ADDITIONS",
  "HUMAN_RESOURCES_STAFF",
  "RSVP",
  "ROOMS",
  "TRAVEL_INSURANCE",
  "SERVICE_SUPPORT",
  "EVENT_INSURANCE",
  "TECHNOLOGY",
  "TRANSLATIONS",
  "TRANSFER",
  "PARKING",
];
type CalculateBudgetItemsKeys = keyof TaxData;
const taxKeys: CalculateBudgetItemsKeys[] = [
  // order is important, fee depends on markup
  "markup",
  "serviceTax",
  "fee",
  "localTax",
  "tourismTax",
  "iof",
  "iss",
  "cbs",
  "ibs",
  "overhead",
  "commission",
  "incentive",
  "tribute",
  "doubleCharged",
];
export function calculateServiceTotals(
  budgetItemsDto: ServiceValues,
  calcOption: CalcOption,
  numberOfServices: number
): ServiceValues {
  if (
    TypesTaxEnum[
      budgetItemsDto.taxData.tariff.typeTax as keyof typeof TypesTaxEnum
    ] === TypesTaxEnum.CORTESY
  ) {
    return budgetItemsDto;
  }
  let inputWithTariff = calculateTariff(budgetItemsDto);
  // inputWithTariff = calculateFee(inputWithTariff);
  let calcResponse: ServiceValues = JSON.parse(JSON.stringify(inputWithTariff));
  calcResponse.totals = {
    totalInBRL:
      calcResponse.totals.totalInBRL +
      inputWithTariff.taxData.tariff.valueBRL +
      inputWithTariff.taxData.fee.valueBRL,
    totalInCurrency:
      calcResponse.totals.totalInCurrency +
      inputWithTariff.taxData.tariff.currencyValue +
      inputWithTariff.taxData.fee.currencyValue,
  };
  // todo - agregar valor total
  for (const key of taxKeys) {
    // skip conditional taxes
    if (
      (budgetItemsDto.exchangeData.currency === "BRL" && key === "iof") ||
      (budgetItemsDto.exchangeData.currency !== "BRL" && key === "iss")
    )
      continue;
    calcResponse = calculateDefaultTax(
      calcResponse,
      key,
      calcOption,
      numberOfServices
    );
  }

  return calcResponse;
}

function tariffPlusMarkup(dto: ServiceValues): number {
  return (
    (dto.taxData.tariff.currencyValue ?? 0) +
    (dto.taxData.markup.currencyValue ?? 0)
  );
}

const TARIFF_PLUS_MARKUP_KEYS = new Set<keyof TaxData>([
  "serviceTax",
  "iss",
  "iof",
  "localTax",
  "commission",
  "incentive",
  "cbs",
  "ibs",
]);

function calculateDefaultTax(
  budgetItemsDto: ServiceValues,
  key: keyof TaxData,
  calcOption: CalcOption,
  numberOfServices: number
): ServiceValues {
  let baseValue = TARIFF_PLUS_MARKUP_KEYS.has(key)
    ? tariffPlusMarkup(budgetItemsDto)
    : budgetItemsDto.taxData.tariff.currencyValue;
  if (key === "iss" && budgetItemsDto.taxData.iss.typeTax === "PERCENTAGE_RJ") {
    baseValue += budgetItemsDto.taxData.serviceTax.currencyValue;
  }
  if (key === "iof") {
    baseValue += budgetItemsDto.taxData.serviceTax.currencyValue;
  }
  if (key === "fee") {
    baseValue += budgetItemsDto.taxData.serviceTax.currencyValue;
    baseValue += budgetItemsDto.taxData.markup.currencyValue;
  }
  if (key === "tribute") {
    baseValue =
      calcOption === "C"
        ? budgetItemsDto.taxData.tariff.value + budgetItemsDto.taxData.fee.value
        : calcOption === "D"
        ? budgetItemsDto.taxData.tariff.value
        : budgetItemsDto.taxData.fee.currencyValue;
  }
  if (key === "doubleCharged") {
    baseValue =
      (budgetItemsDto.taxData.tariff.value +
        budgetItemsDto.taxData.fee.currencyValue) /
      (1 - budgetItemsDto.taxData.doubleCharged.value / 100);
  }
  let updated = budgetItemsDto.taxData[key] as any;

  const { calcBRL, calcConverted } = !budgetItemsDto.appliedTaxes[
    key as keyof AppliedTaxes
  ]
    ? { calcBRL: 0, calcConverted: 0 }
    : key === "doubleCharged"
    ? calcDoubleChargedHelper(
        updated,
        baseValue,
        budgetItemsDto.exchangeData.value,
        numberOfServices
      )
    : ["tribute", "localTax"].includes(key)
    ? calcTributeHelper(
        updated,
        baseValue,
        budgetItemsDto.exchangeData.value,
        numberOfServices
      )
    : calcValueHelper(
        updated,
        baseValue,
        budgetItemsDto.exchangeData.value,
        numberOfServices
      );
  if (
    (budgetItemsDto.exchangeData.currency === "BRL" && key === "iof") ||
    (budgetItemsDto.exchangeData.currency !== "BRL" && key === "iss") ||
    budgetItemsDto.taxData.tariff.typeTax === "CORTESY" ||
    !budgetItemsDto.appliedTaxes[key as keyof AppliedTaxes]
  ) {
    // Keep the user-entered rate (`value`); only clear monetary totals.
    // Zeroing `value` made percentage inputs flash then snap back to 0 after recalc.
    updated = {
      typeTax: updated.typeTax,
      value: updated.value,
      valueBRL: 0,
      currencyValue: 0,
      typeCalculate: updated.typeCalculate,
    };
  } else {
    updated = {
      typeTax: updated.typeTax,
      value: updated.value,
      valueBRL: calcBRL,
      currencyValue: calcConverted,
      typeCalculate: updated.typeCalculate,
    };
  }
  const totalInBRL = budgetItemsDto.totals?.totalInBRL
    ? budgetItemsDto.totals?.totalInBRL + calcBRL
    : calcBRL;
  const totalInCurrency = budgetItemsDto.totals?.totalInCurrency
    ? budgetItemsDto.totals?.totalInCurrency + calcConverted
    : calcConverted;
  const newBudgetItems: ServiceValues = {
    ...budgetItemsDto,
    taxData: {
      ...budgetItemsDto.taxData,
      [key]: updated,
    },
    totals: {
      totalInBRL,
      totalInCurrency,
    },
  };

  return newBudgetItems;
}

function calcTributeHelper(
  inputTax: DefaultTax,
  baseValue: number,
  exchangeValue: number,
  numberOfServices: number
) {
  let calcBRL = 0;
  let calcConverted = 0;
  if (
    TypesTaxEnum[inputTax.typeTax as keyof typeof TypesTaxEnum] ===
    TypesTaxEnum.PERCENTAGE
  ) {
    const calculated =
      (baseValue / (1 - inputTax.value / 100)) * (inputTax.value / 100);
    calcBRL = calculated * exchangeValue;
    calcConverted = calculated;
  } else {
    calcBRL = parseStringToFloat(
      (inputTax.value * exchangeValue) / numberOfServices
    );
    calcConverted = inputTax.value / numberOfServices;
  }
  return { calcBRL, calcConverted };
}

function calcDoubleChargedHelper(
  inputTax: DefaultTax,
  baseValue: number,
  exchangeValue: number,
  numberOfServices: number
) {
  let calcBRL = 0;
  let calcConverted = 0;
  if (
    TypesTaxEnum[inputTax.typeTax as keyof typeof TypesTaxEnum] ===
    TypesTaxEnum.PERCENTAGE
  ) {
    const calculated = baseValue * (inputTax.value / 100);
    calcBRL = calculated * exchangeValue;
    calcConverted = calculated;
  } else {
    calcBRL = parseStringToFloat(
      (inputTax.value * exchangeValue) / numberOfServices
    );
    calcConverted = inputTax.value / numberOfServices;
  }
  return { calcBRL, calcConverted };
}

function calcValueHelper(
  inputTax: DefaultTax,
  baseValue: number,
  exchangeValue: number,
  numberOfServices: number
) {
  let calcBRL = 0;
  let calcConverted = 0;
  if (
    [TypesTaxEnum.PERCENTAGE, TypesTaxEnum.PERCENTAGE_RJ].includes(
      TypesTaxEnum[inputTax.typeTax as keyof typeof TypesTaxEnum]
    )
  ) {
    calcBRL = parseStringToFloat(
      (inputTax.value / 100) * baseValue * exchangeValue
    );
    calcConverted = parseStringToFloat((inputTax.value / 100) * baseValue);
  } else {
    calcBRL = parseStringToFloat(
      (inputTax.value * exchangeValue) / numberOfServices
    );
    calcConverted = inputTax.value / numberOfServices;
  }
  return { calcBRL, calcConverted };
}

function calculateFee(budgetItemsDto: ServiceValues): ServiceValues {
  const newBudgetItems = { ...budgetItemsDto };
  if (!budgetItemsDto.appliedTaxes.fee) {
    newBudgetItems.taxData.fee = {
      typeTax: budgetItemsDto.taxData.fee.typeTax,
      value: budgetItemsDto.taxData.fee.value,
      valueBRL: 0,
      currencyValue: 0,
    };
  }
  if (
    TypesTaxEnum[
      budgetItemsDto.taxData.fee.typeTax as keyof typeof TypesTaxEnum
    ] === TypesTaxEnum.PERCENTAGE
  ) {
    const baseValue = budgetItemsDto.taxData.tariff.currencyValue; // honorarios são calculados somente usando tarifa como base

    newBudgetItems.taxData.fee = {
      typeTax: budgetItemsDto.taxData.fee.typeTax,
      value: budgetItemsDto.taxData.fee.value,
      valueBRL: parseStringToFloat(
        (budgetItemsDto.taxData.fee.value / 100) *
          baseValue *
          budgetItemsDto.exchangeData.value
      ),
      currencyValue: parseStringToFloat(
        (budgetItemsDto.taxData.fee.value / 100) * baseValue
      ),
    };
  } else {
    newBudgetItems.taxData.fee = {
      typeTax: budgetItemsDto.taxData.fee.typeTax,
      value: budgetItemsDto.taxData.fee.value,
      valueBRL: parseStringToFloat(
        budgetItemsDto.taxData.fee.value * budgetItemsDto.exchangeData.value
      ),
      currencyValue: budgetItemsDto.taxData.fee.value,
    };
  }

  return newBudgetItems;
}

function parseStringToFloat(value: number) {
  // Mantém precisão total para suportar câmbio com 4 casas decimais
  // Arredonda apenas para evitar erros de ponto flutuante, mas mantém mais precisão
  return Math.round(value * 10000) / 10000;
}

function calculateTariff(budgetItemsDto: ServiceValues): ServiceValues {
  const newBudgetItems = budgetItemsDto;

  if (
    TypesTaxEnum[
      budgetItemsDto.taxData.tariff?.typeTax as keyof typeof TypesTaxEnum
    ] === TypesTaxEnum.CORTESY
  ) {
    newBudgetItems.taxData.tariff["valueBRL"] = 0;
    newBudgetItems.taxData.tariff["currencyValue"] = 0;
  } else if (
    TypesTaxEnum[
      budgetItemsDto.taxData.tariff.typeTax as keyof typeof TypesTaxEnum
    ] === TypesTaxEnum.DAILY
  ) {
    const value =
      budgetItemsDto.taxData.tariff.value *
      budgetItemsDto.quantity *
      budgetItemsDto.daily;
    newBudgetItems.taxData.tariff["valueBRL"] = roundHalfUp(
      value * budgetItemsDto.exchangeData.value,
      2
    );

    newBudgetItems.taxData.tariff["currencyValue"] = roundHalfUp(value, 2);
  } else if (
    TypesTaxEnum[
      budgetItemsDto.taxData.tariff.typeTax as keyof typeof TypesTaxEnum
    ] === TypesTaxEnum.SERVICE
  ) {
    const value = budgetItemsDto.taxData.tariff.value * budgetItemsDto.quantity;
    newBudgetItems.taxData.tariff["valueBRL"] = roundHalfUp(
      value * budgetItemsDto.exchangeData.value,
      2
    );

    newBudgetItems.taxData.tariff["currencyValue"] = roundHalfUp(value, 2);
  } else {
    const value = budgetItemsDto.taxData.tariff.value;
    newBudgetItems.taxData.tariff["valueBRL"] = roundHalfUp(
      value * budgetItemsDto.exchangeData.value,
      2
    );

    newBudgetItems.taxData.tariff["currencyValue"] = roundHalfUp(value, 2);
  }

  return newBudgetItems;
}
